import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';

@Directive({
  selector: '[appTooltip]'
})
export class TooltipDirective implements OnInit, OnDestroy {
  @Input('appTooltip') tooltipText = '';
  @Input() position: 'top' | 'bottom' | 'left' | 'right' = 'top';
  @Input() delay = 500;

  private tooltipElement: HTMLElement | null = null;
  private mouseEnterListener: (() => void) | null = null;
  private mouseLeaveListener: (() => void) | null = null;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.mouseEnterListener = this.renderer.listen(this.el.nativeElement, 'mouseenter', () => {
      this.showTooltip();
    });

    this.mouseLeaveListener = this.renderer.listen(this.el.nativeElement, 'mouseleave', () => {
      this.hideTooltip();
    });
  }

  ngOnDestroy(): void {
    this.hideTooltip();

    if (this.mouseEnterListener) {
      this.mouseEnterListener();
    }

    if (this.mouseLeaveListener) {
      this.mouseLeaveListener();
    }
  }

  private showTooltip(): void {
    if (!this.tooltipText) {
      return;
    }

    this.tooltipElement = this.renderer.createElement('div');
    this.renderer.addClass(this.tooltipElement, 'app-tooltip');

    const text = this.renderer.createText(this.tooltipText);
    this.renderer.appendChild(this.tooltipElement, text);

    this.renderer.setStyle(this.tooltipElement, 'position', 'fixed');
    this.renderer.setStyle(this.tooltipElement, 'background', 'rgba(0, 0, 0, 0.85)');
    this.renderer.setStyle(this.tooltipElement, 'color', 'white');
    this.renderer.setStyle(this.tooltipElement, 'padding', '5px 10px');
    this.renderer.setStyle(this.tooltipElement, 'border-radius', '4px');
    this.renderer.setStyle(this.tooltipElement, 'font-size', '12px');
    this.renderer.setStyle(this.tooltipElement, 'z-index', '10000');
    this.renderer.setStyle(this.tooltipElement, 'max-width', '300px');
    this.renderer.setStyle(this.tooltipElement, 'word-wrap', 'break-word');

    this.renderer.appendChild(document.body, this.tooltipElement);

    this.updateTooltipPosition();

    window.addEventListener('scroll', this.updateTooltipPosition.bind(this), true);
    window.addEventListener('resize', this.updateTooltipPosition.bind(this));
  }

  private updateTooltipPosition = (): void => {
    if (!this.tooltipElement) {
      return;
    }

    const hostPos = this.el.nativeElement.getBoundingClientRect();
    const tooltipPos = this.tooltipElement.getBoundingClientRect();

    let top, left;

    switch (this.position) {
      case 'top':
        top = hostPos.top - tooltipPos.height - 10;
        left = hostPos.left + hostPos.width / 2 - tooltipPos.width / 2;
        break;
      case 'bottom':
        top = hostPos.bottom + 10;
        left = hostPos.left + hostPos.width / 2 - tooltipPos.width / 2;
        break;
      case 'left':
        top = hostPos.top + hostPos.height / 2 - tooltipPos.height / 2;
        left = hostPos.left - tooltipPos.width - 10;
        break;
      case 'right':
        top = hostPos.top + hostPos.height / 2 - tooltipPos.height / 2;
        left = hostPos.right + 10;
        break;
    }

    if (top < 0) {
      top = 5;
    }
    if (left < 0) {
      left = 5;
    }
    if (left + tooltipPos.width > window.innerWidth) {
      left = window.innerWidth - tooltipPos.width - 5;
    }

    this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
  };

  private hideTooltip(): void {
    if (this.tooltipElement) {
      window.removeEventListener('scroll', this.updateTooltipPosition.bind(this), true);
      window.removeEventListener('resize', this.updateTooltipPosition.bind(this));
      this.renderer.removeChild(document.body, this.tooltipElement);
      this.tooltipElement = null;
    }
  }
}
